import React from 'react';

function confidenceColor(label = '') {
  const normalized = String(label).toUpperCase();
  if (normalized === 'HIGH') return '#22c55e';
  if (normalized === 'MODERATE') return '#22c55e';
  if (normalized === 'LOW') return '#eab308';
  return '#ef4444';
}

function friendlyStatus(status = '') {
  const text = String(status).toLowerCase();
  if (text.includes('recovered last clean')) return 'Verified backup snapshot';
  if (text.includes('configured baseline')) return 'Safety baseline';
  if (text.includes('daily') || text.includes('latest')) return status;
  if (text.includes('live')) return 'Live';
  if (text.includes('min old') || text.includes('hr old') || text.includes('day old')) return `Recent · ${status}`;
  if (text.includes('estimated')) return 'Estimated';
  if (text.includes('optional')) return status;
  if (text.includes('unavailable')) return 'Feed unavailable';
  return status || 'Unknown';
}


function friendlySource(source = '') {
  const text = String(source || '').trim();
  const lower = text.toLowerCase();
  if (!text) return 'Source details unavailable';
  if (lower.includes('recovered_from_clean_snapshot') || lower.includes('last_clean_daily_snapshot')) return 'Verified daily backup snapshot';
  if (lower.includes('estimate_from_vix')) return 'Estimated from stock-volatility data';
  if (lower.includes('fred:vixcls')) return 'FRED VIX close';
  if (lower.includes('fred treasury')) return 'FRED Treasury yields';
  if (lower.includes('configured safety')) return 'Configured safety baseline';
  return text.replaceAll('_', ' ').replace(/\s+/g, ' ');
}

function friendlyConfidenceLabel(label = '', score) {
  const normalized = String(label).toUpperCase();
  const numeric = Number(score);
  if (normalized === 'MODERATE' && Number.isFinite(numeric) && numeric >= 65) return 'MODERATE+ ';
  return normalized;
}

function statusColor(status = '') {
  const text = String(status).toLowerCase();
  if (text.includes('live') || text.includes('latest') || text.includes('daily')) return '#22c55e';
  if (text.includes('recovered') || text.includes('backup') || text.includes('baseline') || text.includes('estimated') || text.includes('optional') || text.includes('old')) return '#eab308';
  if (text.includes('unavailable')) return '#ef4444';
  return '#94a3b8';
}


function factorColor(item = {}) {
  const factor = Number(item.factor);
  if (Number.isFinite(factor) && factor >= 0.9) return '#22c55e';
  if (Number.isFinite(factor) && factor >= 0.65) return '#eab308';
  if (Number.isFinite(factor) && factor >= 0.4) return '#f97316';
  return '#ef4444';
}

function confidenceHeadline(label = '', score) {
  const n = Number(score);
  if (String(label).toUpperCase() === 'HIGH') return 'Core systems operational';
  if (String(label).toUpperCase() === 'MODERATE') return 'Core systems operational with some delayed feeds';
  if (String(label).toUpperCase() === 'LOW') return Number.isFinite(n) && n >= 55
    ? 'Usable guidance with degraded live feeds'
    : 'Limited-confidence guidance';
  return 'Major feed outage — use caution';
}

export default function DataFreshnessConfidence({ confidence, freshness = [] }) {
  if (!confidence && !freshness.length) return null;

  const label = confidence?.label || 'UNKNOWN';
  const color = confidenceColor(label);
  const score = confidence?.score;

  return (
    <div className="glass-card p-6 animate-slide-up" style={{ animationDelay: '0.35s' }}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display font-bold text-lg text-mentor-text">
            Data Freshness / Signal Confidence
          </h3>
          <p className="text-sm text-mentor-muted font-body mt-1">
            Shows whether Bond Mentor is using live feeds, verified backup snapshots, estimates, or optional sources.
          </p>
          <p className="text-sm text-mentor-text/90 font-body mt-2">
            {confidenceHeadline(label, score)}
          </p>
        </div>
        <div className="rounded-xl px-4 py-2 border" style={{ borderColor: `${color}55`, backgroundColor: `${color}12` }}>
          <p className="text-[10px] text-mentor-muted font-display uppercase tracking-widest">Signal Confidence</p>
          <p className="font-display font-bold text-sm" style={{ color }}>
            {friendlyConfidenceLabel(label, score)}{Number.isFinite(Number(score)) ? ` · ${score}/100` : ''}
          </p>
        </div>
      </div>

      {confidence?.description && (
        <p className="text-sm text-mentor-text/85 font-body mb-4">
          {confidence.description}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {freshness.map((feed) => {
          const displayStatus = friendlyStatus(feed.status);
          const colorForStatus = statusColor(`${feed.status} ${displayStatus}`);
          return (
            <div key={feed.key || feed.label} className="rounded-xl border border-mentor-border/60 bg-mentor-bg/35 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs text-mentor-muted font-body uppercase tracking-wider">{feed.label}</p>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorForStatus, boxShadow: `0 0 8px ${colorForStatus}55` }} />
              </div>
              <p className="font-mono text-sm font-semibold" style={{ color: colorForStatus }}>
                {displayStatus}
              </p>
              <p className="text-[11px] text-mentor-muted font-body mt-1 truncate" title={friendlySource(feed.source)}>
                {friendlySource(feed.source)}
              </p>
            </div>
          );
        })}
      </div>

      {!!confidence?.breakdown?.length && (
        <div className="mt-5 rounded-2xl border border-mentor-border/50 bg-mentor-bg/30 p-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
            <div>
              <h4 className="font-display font-bold text-mentor-text">Confidence Weighting Engine</h4>
            </div>
            <p className="text-xs text-mentor-muted">Core feeds carry more weight; optional feeds do not break the dashboard.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {confidence.breakdown.map((item) => {
              const color = factorColor(item);
              return (
                <div key={item.key} className="rounded-xl border border-mentor-border/40 bg-mentor-bg/35 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs text-mentor-muted uppercase tracking-wider">{item.label}</p>
                    <span className="text-[10px] font-mono" style={{ color }}>{item.points}/{item.weight}</span>
                  </div>
                  <p className="font-mono text-sm font-semibold mt-1" style={{ color }}>{item.status}</p>
                  <p className="text-[11px] text-mentor-muted mt-1 leading-relaxed">{item.detail}</p>
                </div>
              );
            })}
          </div>
          {!!confidence.adjustments?.length && (
            <div className="mt-3 text-xs text-mentor-muted leading-relaxed">
              {confidence.adjustments.map((adjustment, index) => (
                <p key={`${adjustment.label}-${index}`}>• {adjustment.label}: {adjustment.detail}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
